import myAxios from '@/request/myAxios';
import { BaseDTO } from '@/request/apiConfig';
import {
  AppInfoDTO,
  CompanyConfigDTO,
  Department,
  DepartmentUser,
  MsgAuditApplicationInfoDTO,
  UseridOpenidReturnDTO,
  UserMsgAuditInfo,
  UserMsgAuditInfoPageDTO,
  UserMsgAuditInfoQuery,
} from '@/dto/company';
import {
  ChatRequestDTO,
  DepartmentUserQueryDTO,
  ExportQueryDTO,
  PageDTO,
  PageRequestDTO,
} from '@/dto/page';
import { ChatDataDTO, ChatMembersDTO } from '@/dto/chat';
import { CosConfigDTO, CosCredentialsDTO } from '@/dto/mediaData';
import { ExternalRoomDTO } from '@/dto/room';
import { store } from '@/store';
import { AutoRoleCustomOptionsDTO, AutoRoleDTO, RoleSaveResult, UserRoleDTO } from '@/dto/role';
import { AutoUserDTO } from '@/dto/user';
import {
  AutoDataPermission,
  AutoDataPermissionUser,
  AutoUserDataPermissionSaveDTO,
} from '@/dto/dataPermission';
import { UserBehaviorDataDTO, UserBehaviorDataQuery } from '@/dto/statisticData';
import { apiChatLog } from '@/request/apiChatLog';
import {
  KeywordLogPageDTO,
  KeywordLogQuery,
  KeywordNoticeDTO,
  KeywordQuery,
  MsgKeyword,
} from '../dto/keyword';
import { ExportDataDTO, ExportDataRequestDTO } from '../dto/export';
import { apiExternal } from '@/request/apiExternal';
import { RemarkDTO, RemarkRequestDTO } from '@/dto/remark';
import { CompanyReplyConfigDTO, ReplyLogPageDTO, ReplyLogQueryDTO } from '@/dto/replyTimeout';
import { ReqRuleSave, RuleGetResult } from '@/dto/rule';
import { SpaceAuthInfo } from '@/dto/space';

const AUTH_CONTEXT_PATH = import.meta.env.VITE_AUTH_CONTEXT_PATH;
const MAIN_CONTEXT_PATH = import.meta.env.VITE_MAIN_CONTEXT_PATH;

// 会话查询
const chat = {
  companyChatList: (query: PageRequestDTO): Promise<BaseDTO<PageDTO<ChatDataDTO>>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/chat/list/all`, query),
  privateChatList: (query: ChatRequestDTO): Promise<BaseDTO<PageDTO<ChatDataDTO>>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/chat/private`, query),
  roomChatList: (query: ChatRequestDTO): Promise<BaseDTO<PageDTO<ChatDataDTO>>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/chat/room`, query),
  allPrivateChat: (query: ChatRequestDTO): Promise<BaseDTO<PageDTO<ChatDataDTO>>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/chat/all/private`, query),
  allPrivateChatRevoke: (query: ChatRequestDTO): Promise<BaseDTO<PageDTO<ChatDataDTO>>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/chat/revoke/all/private`, query),
  allRoomChat: (query: ChatRequestDTO): Promise<BaseDTO<PageDTO<ChatDataDTO>>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/chat/all/room`, query),
  allRoomChatRevoke: (query: ChatRequestDTO): Promise<BaseDTO<PageDTO<ChatDataDTO>>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/chat/revoke/all/room`, query),
  // 获取聊天成员
  allPrivateChatMembers: (query: ChatRequestDTO): Promise<BaseDTO<ChatMembersDTO>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/chat/all/private/chatMembers`, query),
  allRoomChatMembers: (query: ChatRequestDTO): Promise<BaseDTO<ChatMembersDTO>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/chat/all/room/chatMembers`, query),
  allRoom: (query: ChatRequestDTO): Promise<BaseDTO<ChatMembersDTO>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/chat/all/roomChatMembers`, query),
  chatInfo: (query: ChatRequestDTO): Promise<BaseDTO<ChatRequestDTO>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/chat/all/chatInfo`, query),
};

const exportChat = {
  createExport: (query: ExportDataRequestDTO): Promise<BaseDTO<void>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/export`, query),
  exportList: (query: ExportQueryDTO): Promise<BaseDTO<PageDTO<ExportDataDTO>>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/export/list`, query),
};

const room = {
  /**
   * 同步客户群信息
   * @param roomid
   * @returns
   */
  externalRoom: (roomid: string): Promise<BaseDTO<ExternalRoomDTO>> =>
    myAxios.get(`${MAIN_CONTEXT_PATH}/room/external/${roomid}`),
};

const keyword = {
  keywordList: (query: KeywordQuery): Promise<BaseDTO<PageDTO<MsgKeyword>>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/keyword/list`, query),
  allKeyword: (): Promise<BaseDTO<string[]>> => myAxios.get(`${MAIN_CONTEXT_PATH}/keyword/list`),
  // insertKeyword: (k: string): Promise<BaseDTO<MsgKeyword>> =>
  //   myAxios.post(`${MAIN_CONTEXT_PATH}/keyword/insert/${k}`),
  insertKeyword: (k: string): Promise<BaseDTO<MsgKeyword>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/keyword/insert`, { keyword: k }),
  deleteKeyword: (id: string): Promise<BaseDTO<number>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/keyword/delete/${id}`),
  keywordLog: (query: KeywordLogQuery): Promise<BaseDTO<KeywordLogPageDTO>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/keyword/log/list`, query),
  deleteKeywordLog: (id: string) => myAxios.post(`${MAIN_CONTEXT_PATH}/keyword/log/delete/${id}`),
  keywordLogChatInfo: (id: string): Promise<BaseDTO<ChatRequestDTO>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/keyword/log/chatInfo/${id}`),
  allKeywordNoticeConfig: (): Promise<BaseDTO<KeywordNoticeDTO[]>> =>
    myAxios.get(`${MAIN_CONTEXT_PATH}/keyword/notice/config`),
  createKeywordNoticeConfig: (name: string): Promise<BaseDTO<KeywordNoticeDTO>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/keyword/notice/config/create`, { name }),
  updateKeywordNoticeConfig: (data: { id: string; noticeUser: string[]; triggerUser: string[] }) =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/keyword/notice/config/update`, data),
  deleteKeywordNoticeConfig: (id: string) =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/keyword/notice/config/delete/${id}`),
  getReplyConfig: (): Promise<BaseDTO<CompanyReplyConfigDTO>> =>
    myAxios.get(`${MAIN_CONTEXT_PATH}/reply/timeout/setting`),
  saveReplyConfig: (data: CompanyReplyConfigDTO): Promise<BaseDTO<CompanyReplyConfigDTO>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/reply/timeout/setting`, data),
  queryReplyLog: (data: ReplyLogQueryDTO): Promise<BaseDTO<ReplyLogPageDTO>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/reply/timeout/log`, data),
  ruleSave: (data: ReqRuleSave): Promise<BaseDTO<{ rule_id: string }>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/keyword/rule/save`, data),
  ruleDetail: (ruleId: string): Promise<BaseDTO<ReqRuleSave>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/keyword/rule/detail`, {
      rule_id: ruleId,
    }),
  ruleList: (cursor: string, limit: number): Promise<BaseDTO<RuleGetResult>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/keyword/rule/list`, {
      cursor,
      limit,
    }),
  ruleDelete: (ruleId: string): Promise<BaseDTO<any>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/keyword/rule/delete`, {
      rule_id: ruleId,
    }),
};

const department = {
  getDepartmentList: async (): Promise<BaseDTO<{ department: Department[] }>> => {
    if (store.state.departmentList && store.state.departmentList.length > 0) {
      return Promise.resolve({
        status: 1,
        msg: '',
        isSuccess: () => true,
        data: {
          department: store.state.departmentList,
        },
      });
    }
    const res: BaseDTO<{ department: Department[] }> = await myAxios.get(
      `${AUTH_CONTEXT_PATH}/company/department/list`,
    );
    store.commit('setDepartmentList', res.data.department);
    return res;
  },
  getDepartmentUserList: async (id: number): Promise<BaseDTO<{ userlist: DepartmentUser[] }>> => {
    console.log('api', store.state.departmentUserList);

    if (store.state.departmentUserList.length > 0) {
      return Promise.resolve({
        status: 1,
        msg: '',
        isSuccess: () => true,
        data: {
          userlist: store.state.departmentUserList,
        },
      });
    }
    const res: BaseDTO<{ userlist: DepartmentUser[] }> = await myAxios.get(
      `${AUTH_CONTEXT_PATH}/company/department/${id}/user/list?refresh=false`,
    );
    store.commit('setDepartmentUserList', res.data.userlist);
    // if (res.data.userlist.length <= 0) {
    //   res = await myAxios.get(`${AUTH_CONTEXT_PATH}/company/department/${id}/user/list?refresh=true`);
    // }
    return res;
  },
  postDepartmentUserList: (
    query: DepartmentUserQueryDTO,
  ): Promise<BaseDTO<PageDTO<DepartmentUser>>> =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/company/department/user/list`, query),
  getMsgAuditUserList: (): Promise<
    BaseDTO<{
      userlist: DepartmentUser[];
      permitlist: UserMsgAuditInfo[];
    }>
  > => myAxios.post(`${AUTH_CONTEXT_PATH}/company/department/msgAudit/user/list`),
  getMsgAuditUserTable: (query: UserMsgAuditInfoQuery): Promise<BaseDTO<UserMsgAuditInfoPageDTO>> =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/company/department/msgAudit/user/table`, query),
  refreshMsgAuditUser: (): Promise<BaseDTO<any>> =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/company/department/msgAudit/user/refresh`),
  searchDepartmentUser: (name: string): Promise<BaseDTO<{ userlist: DepartmentUser[] }>> =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/company/department/search`, { name }),
  syncDepartment: () => myAxios.post(`${AUTH_CONTEXT_PATH}/company/department/sync`),
  postDepartmentRoomUser: (roomid: string): Promise<BaseDTO<{ [k: string]: DepartmentUser }>> =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/company/department/room/user`, { roomid }),
  postDepartmentUsers: (users: string[]): Promise<BaseDTO<{ [k: string]: DepartmentUser }>> =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/company/department/users`, users),
  useridOpenid: (): Promise<BaseDTO<UseridOpenidReturnDTO>> =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/company/department/openUserid`),
  useridOpenidByUserids: (list: string[]): Promise<BaseDTO<UseridOpenidReturnDTO>> =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/company/department/openUserid/list`, list),
};

const role = {
  roleCustomOptions: (): Promise<BaseDTO<AutoRoleCustomOptionsDTO[]>> =>
    myAxios.get(`${AUTH_CONTEXT_PATH}/admin/role/custom/options`),
  roleCustomSave: (data: AutoRoleDTO): Promise<BaseDTO<AutoRoleDTO>> =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/admin/role/custom/save`, data),
  roleCustomDelete: (name: string) =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/admin/role/custom/${name}/delete`),
  roleCustomList: (): Promise<BaseDTO<AutoRoleDTO[]>> =>
    myAxios.get(`${AUTH_CONTEXT_PATH}/admin/role/custom/list`),
  roleUserSave: (data: UserRoleDTO) =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/admin/role/user/save`, data),
  roleUserSaveBatch: (data: UserRoleDTO[]) =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/admin/role/user/save/batch`, data),
  roleUserGet: (wxUserid: string): Promise<BaseDTO<AutoRoleDTO>> =>
    myAxios.get(`${AUTH_CONTEXT_PATH}/admin/role/user/${wxUserid}`),
};

const admin = {
  roleUserSave: (): Promise<BaseDTO<SpaceAuthInfo>> =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/admin/space/authInfo`),
};

const dataPermission = {
  dataPermissionList: (): Promise<BaseDTO<AutoDataPermission[]>> =>
    myAxios.get(`${AUTH_CONTEXT_PATH}/dataPermission/list`),
  dataPermissionCreate: (data: { name: string }): Promise<BaseDTO<AutoDataPermission>> =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/dataPermission/create`, data),
  dataPermissionDelete: (id: string) =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/dataPermission/delete/${id}`),

  dataPermissionUpdateName: (data: { id: string; name: string }) =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/dataPermission/update/name`, data),
  dataPermissionUpdateMembers: (data: { id: string; members: string[] }) =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/dataPermission/update/members`, data),
  dataPermissionUpdateAdmins: (data: { id: string; members: string[] }) =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/dataPermission/update/admins`, data),
  dataPermissionListByUser: (wxUserid: string): Promise<BaseDTO<AutoDataPermissionUser[]>> =>
    myAxios.get(`${AUTH_CONTEXT_PATH}/dataPermission/user/${wxUserid}`),
  dataPermissionListByUserUpdate: (wxUserid: string, permissions: string[]) =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/dataPermission/user/${wxUserid}`, permissions),
  dataPermissionUpdataAdminBatch: (
    data: AutoUserDataPermissionSaveDTO[],
  ): Promise<BaseDTO<RoleSaveResult>> =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/dataPermission/user/update/batch`, data),
};

const statisticData = {
  userBehaviorData: (
    query: UserBehaviorDataQuery,
  ): Promise<BaseDTO<{ behavior_data: UserBehaviorDataDTO[] }>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/statisticData/userBehaviorData`, query),
  keyWordCount: (
    timeSliceType: string,
    startTime: number,
    endTime: number,
  ): Promise<BaseDTO<object>> =>
    myAxios.get(`${MAIN_CONTEXT_PATH}/statisticData/keywordCount`, {
      params: { timeSliceType, startTime, endTime },
    }),
};

const remark = {
  postRemark: (remarkId: string): Promise<BaseDTO<RemarkDTO>> =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/remark/one`, { remarkId }),
  saveRemark: (dto: RemarkRequestDTO): Promise<BaseDTO<any>> =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/remark/save`, dto),
};
export const api = {
  ...chat,
  ...exportChat,
  ...room,
  ...keyword,
  ...department,
  ...role,
  ...dataPermission,
  ...apiExternal,
  ...apiChatLog,
  ...remark,
  ...statisticData,
  admin,
  logout: () => myAxios.post(`${AUTH_CONTEXT_PATH}/logout`),
  loginUserInfo: (): Promise<BaseDTO<AutoUserDTO>> =>
    myAxios.get(`${AUTH_CONTEXT_PATH}/login/info`),
  getMsgAuditApplicationInfo: (): Promise<BaseDTO<MsgAuditApplicationInfoDTO>> =>
    myAxios.get(`${AUTH_CONTEXT_PATH}/company/msgAuditAppInfo`),
  postMsgAuditApplicationInfo: (data: MsgAuditApplicationInfoDTO): Promise<BaseDTO<null>> =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/company/msgAuditAppInfo`, data),
  initMsgAuditApplicationInfo: (): Promise<BaseDTO<MsgAuditApplicationInfoDTO>> =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/company/msgAuditAppInfo/init`),
  appInfoKey: (): Promise<BaseDTO<AppInfoDTO>> =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/company/appInfo/key`),
  saveMsgAuditApplicationInfoToken: (data: MsgAuditApplicationInfoDTO) =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/company/msgAuditAppInfo/token`, data),
  saveCorpid: (data: AppInfoDTO) => myAxios.post(`${AUTH_CONTEXT_PATH}/company/corpid`, data),
  checkMsgAuditApplicationServerState: (): Promise<BaseDTO<boolean>> =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/company/msgAuditAppInfo/state`),
  serverState: (applicationId: string): Promise<BaseDTO<boolean>> =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/company/appInfo/${applicationId}/serverState`),
  secretState: (applicationId: string): Promise<BaseDTO<boolean>> =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/company/appInfo/${applicationId}/secretState`),
  saveAppInfo: (data: AppInfoDTO): Promise<BaseDTO<AppInfoDTO>> =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/company/appInfo`, data),
  saveAppInfoSecret: (data: AppInfoDTO): Promise<BaseDTO<AppInfoDTO>> =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/company/appInfo/secret`, data),
  saveAppInfoToken: (data: AppInfoDTO): Promise<BaseDTO<AppInfoDTO>> =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/company/appInfo/token`, data),
  getAppInfo: (applicationId: string): Promise<BaseDTO<AppInfoDTO>> =>
    myAxios.get(`${AUTH_CONTEXT_PATH}/company/appInfo/${applicationId}`),
  companyConfig: (): Promise<BaseDTO<CompanyConfigDTO>> =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/company/config`),
  setExternalContactApplicationId: (applicationId: string): Promise<BaseDTO<any>> =>
    myAxios.post(`${AUTH_CONTEXT_PATH}/company/appInfo/externalContact/${applicationId}`),

  // getExternalContactByUser: (userid: string): Promise<BaseDTO<ExternalContactDTO[]>> =>
  //   myAxios.get(`${AUTH_CONTEXT_PATH}/company/externalContact/${userid}`),
  // 媒体文件相关
  getMediaDataCredential: (): Promise<BaseDTO<CosCredentialsDTO>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/mediaData/credential`),
  getMediaDataConfig: (): Promise<BaseDTO<CosConfigDTO>> =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/mediaData/config`),
  getMediaDataUrl: (
    dirs: string[],
    style: string,
  ): Promise<
    BaseDTO<{
      [k: string]: string;
    }>
  > =>
    myAxios.post(`${MAIN_CONTEXT_PATH}/mediaData/url`, dirs, {
      params: {
        style,
      },
    }),
};
